// Vault em disco (desktop/Tauri): cada nota é um arquivo numa pasta real
// escolhida pelo usuário (estilo Obsidian) — `.md` para texto e
// `.excalidraw` / `.excalidraw.md` para desenho. Usa os plugins Tauri dialog +
// fs, carregados sob demanda (não entram no bundle web).
import type { Note } from './vault'
import { ehArquivoDeDesenho } from './excalidraw'

export function isTauri(): boolean {
  return typeof window !== 'undefined' && ('__TAURI_INTERNALS__' in window || '__TAURI__' in window)
}

/** App NATIVO empacotado: desktop (Tauri) ou mobile (Capacitor). Na WEB (PWA/
 *  navegador) retorna false — lá o uso é obrigatoriamente com conta online. */
export function isNativeApp(): boolean {
  if (isTauri()) return true
  const cap = typeof window !== 'undefined' ? (window as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor : undefined
  return !!(cap && typeof cap.isNativePlatform === 'function' && cap.isNativePlatform())
}

const uid = () => 'n_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36)
const slug = (s: string) => (s || 'nota').replace(/[\\/:*?"<>|]/g, '-').replace(/\s+/g, ' ').trim() || 'nota'

/** Extensões que o vault reconhece como conteúdo.
 *  `.txt` entrou em 20/09/2026: um cofre real tem anotação em texto puro, e
 *  ignorá-la significava que ela não existia para a busca nem para o grafo. */
export const EXTENSOES = /\.(md|markdown|txt|excalidraw)$/i

/** Extensões que o TÍTULO carrega, porque é o nome do arquivo que manda.
 *  Para `.md` o título é limpo (é o formato padrão do cofre); para as outras,
 *  tirar a extensão do título faria o `relPathOf` devolver outro nome de
 *  arquivo — e "editar o .txt" viraria "criar um .md ao lado, e abandonar o
 *  .txt". Ver `tipoDoArquivo`. */
const EXT_NO_TITULO = /\.(txt)$/i

/** Caminho relativo (dentro da pasta do vault) do arquivo de uma nota.
 *  Desenho já carrega a extensão no próprio título (ex.: "Fluxo.excalidraw"),
 *  do mesmo jeito que o plugin do Obsidian faz — então não acrescentamos .md.
 *  O mesmo vale para `.txt`: sem esta checagem, salvar `notas.txt` gravava
 *  `notas.txt.md` e deixava o arquivo original órfão. */
export function relPathOf(n: { title: string; folder?: string; kind?: 'draw' }): string {
  const nome = slug(n.title)
  const jaTemExtensao = n.kind === 'draw' || ehArquivoDeDesenho(nome) || EXT_NO_TITULO.test(nome)
  return `${folderKey(n.folder)}${nome}${jaTemExtensao ? '' : '.md'}`
}

/** Título e tipo a partir do nome do arquivo. */
export function tipoDoArquivo(nomeOuCaminho: string): { title: string; kind?: 'draw' } {
  const base = nomeOuCaminho.slice(nomeOuCaminho.lastIndexOf('/') + 1)
  if (ehArquivoDeDesenho(base)) {
    // Preserva o nome COMPLETO, inclusive o ".md" de um .excalidraw.md — é ele
    // que diz em qual dos dois formatos o arquivo deve ser regravado, e é o que
    // faz o relPathOf reproduzir exatamente o mesmo nome de arquivo.
    return { title: base, kind: 'draw' }
  }
  // Só o .md/.markdown perde a extensão no título — é o formato nativo do
  // cofre. `.txt` conserva a sua, e é isso que faz o `relPathOf` reproduzir o
  // MESMO nome de arquivo em vez de inventar um `.md` ao lado.
  return { title: base.replace(/\.(md|markdown)$/i, '') }
}

/** Prefixo (chave) de uma pasta no mesmo esquema do relPathOf.
 *  Ex.: "Estudos/IA" → "estudos/ia/". Raiz/vazio → "". Usado para
 *  compartilhar uma pasta inteira (o prefixo casa com tudo dentro dela). */
export function folderKey(folder?: string): string {
  return folder ? folder.split('/').filter(Boolean).map(slug).join('/') + '/' : ''
}

/** Abre o seletor de pasta nativo; devolve o caminho ou null se cancelar. */
export async function pickFolder(): Promise<string | null> {
  const { open } = await import('@tauri-apps/plugin-dialog')
  const res = await open({ directory: true, multiple: false, title: 'Escolher pasta do cofre' })
  return typeof res === 'string' ? res : null
}

// --- Navegador de pastas próprio (mostra só diretórios) --------------------
export async function homeDir(): Promise<string> {
  const { homeDir } = await import('@tauri-apps/api/path')
  return (await homeDir()).replace(/\/+$/, '')
}
/** Lista só os subdiretórios (ignora ocultos), ordenados. */
export async function listSubdirs(path: string): Promise<string[]> {
  const fs = await import('@tauri-apps/plugin-fs')
  try {
    const entries = (await fs.readDir(path)) as { name: string; isDirectory: boolean }[]
    return entries
      .filter((e) => e.isDirectory && !e.name.startsWith('.'))
      .map((e) => e.name)
      .sort((a, b) => a.localeCompare(b))
  } catch {
    return []
  }
}
/** Cria uma pasta (recursivo). */
export async function makeDir(path: string): Promise<void> {
  const fs = await import('@tauri-apps/plugin-fs')
  await fs.mkdir(path, { recursive: true }).catch(() => {})
}

/** Lê recursivamente os .md de uma pasta. Devolve as notas E o caminho real
 *  (relativo) de cada arquivo — preservar o nome original evita renomeações
 *  acidentais (ex.: ao editar uma nota cujo arquivo tem caracteres especiais). */
export async function readVaultDir(dir: string): Promise<{ notes: Note[]; rels: Map<string, string> }> {
  const fs = await import('@tauri-apps/plugin-fs')
  // 1) Coleta todos os .md (caminhada de pastas em paralelo).
  const files: { abs: string; rel: string }[] = []
  async function collect(abs: string, rel: string) {
    let entries: { name: string; isDirectory: boolean; isFile: boolean; isSymlink?: boolean }[]
    try {
      entries = (await fs.readDir(abs)) as typeof entries
    } catch {
      return
    }
    const subdirs: [string, string][] = []
    for (const e of entries) {
      if (e.name.startsWith('.')) continue // ignora .obsidian, .trash, .DS_Store, .sinapse…
      const childAbs = `${abs}/${e.name}`
      const childRel = rel ? `${rel}/${e.name}` : e.name
      if (e.isDirectory) subdirs.push([childAbs, childRel])
      else if (EXTENSOES.test(e.name)) files.push({ abs: childAbs, rel: childRel })
    }
    await Promise.all(subdirs.map(([a, r]) => collect(a, r))) // subpastas em paralelo
  }
  await collect(dir, '')

  // 2) Lê o conteúdo de todos os arquivos em paralelo (em lotes, p/ não saturar o IPC).
  const notes: Note[] = []
  const rels = new Map<string, string>()
  const BATCH = 32
  for (let i = 0; i < files.length; i += BATCH) {
    await Promise.all(
      files.slice(i, i + BATCH).map(async (f) => {
        let body = ''
        try {
          body = await fs.readTextFile(f.abs)
        } catch {
          return // arquivo ilegível → pula (não derruba o resto)
        }
        const now = Date.now()
        const id = uid()
        const slash = f.rel.lastIndexOf('/')
        const { title, kind } = tipoDoArquivo(f.rel)
        notes.push({
          id,
          title,
          ...(kind ? { kind } : {}),
          body,
          folder: slash >= 0 ? f.rel.slice(0, slash) : undefined,
          createdAt: now,
          updatedAt: now,
          lastOpenedAt: now,
          pinned: false,
          favorited: false,
        })
        rels.set(id, f.rel)
      }),
    )
  }
  return { notes, rels }
}

/** Grava o arquivo de uma nota (cria as subpastas necessárias). */
export async function writeNoteFile(dir: string, rel: string, body: string): Promise<void> {
  const fs = await import('@tauri-apps/plugin-fs')
  const slash = rel.lastIndexOf('/')
  if (slash > 0) await fs.mkdir(`${dir}/${rel.slice(0, slash)}`, { recursive: true }).catch(() => {})
  await fs.writeTextFile(`${dir}/${rel}`, body)
}

/** Remove o arquivo de uma nota. */
export async function removeNoteFile(dir: string, rel: string): Promise<void> {
  const fs = await import('@tauri-apps/plugin-fs')
  await fs.remove(`${dir}/${rel}`).catch(() => {})
}

// --- Metadados do Sinapse (.sinapse/meta.json) -----------------------------
// Espelha o .obsidian: guarda o que não cabe no .md (fixado/favorito/recência),
// por caminho de arquivo. Mantém o vault 100% compatível com o Obsidian.
// As marcas da nota (fixado/favorito/recência) NÃO moram mais aqui: elas são
// por pessoa e vivem em core/preferencias.ts, lidas e gravadas pela Pasta
// (core/pasta.ts) no `.sinapse/meta.json`. As funções que este arquivo
// exportava para isso (`readSinapseMeta`/`writeSinapseMeta`, do tempo em que
// o cofre em pasta era só do desktop) já não eram chamadas por ninguém e
// escreviam o formato 1 — que, hoje, apagaria o balde de quem compartilha a
// pasta. Foram removidas em 19/09/2026 junto com a decisão D2 (§11).
